import { FC, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useTranslate } from 'hooks/useTranslate';
import { useDispatch } from 'react-redux';
import { store } from 'redux/store';

import { setModal } from 'modules/_shared/redux/modalActions';
import { updateBoard } from 'modules/boards/redux/boardActions';
import VimeoVideoPlayer from 'modules/_shared/components/VimeoVideoPlayer';
import successSwal from 'utils/successSwal';
import errorSwal from 'utils/errorSwal';

import { BoardVideo } from 'types';

type AddVideoModalProps = {
  boardId: string;
  currentVideos: BoardVideo[];
};

const AddVideoModal: FC<AddVideoModalProps> = ({ boardId, currentVideos }) => {
  const { t } = useTranslate();
  const dispatch = useDispatch();

  const [videoId, setVideoId] = useState('');
  const [title, setTitle] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isValidated, setIsValidated] = useState(false);

  const handleClose = () => {
    dispatch(setModal(null));
  };

  const validateVimeoId = (videoIdToValidate: string) => {
    // Check if video ID is numeric
    if (!/^\d+$/.test(videoIdToValidate.trim())) {
      return {
        valid: false,
        error: t('VideoIdMustBeNumeric') || 'Video ID must be numeric',
      };
    }

    return { valid: true };
  };

  const handleSave = async () => {
    if (!videoId.trim()) {
      errorSwal(t('VideoIdRequired') || 'Video ID is required');
      return;
    }

    if (!title.trim()) {
      errorSwal(t('TitleRequired') || 'Title is required');
      return;
    }

    // Validate video ID before saving
    const validation = validateVimeoId(videoId.trim());

    if (!validation.valid) {
      errorSwal(validation.error || t('InvalidVideoId') || 'Invalid video ID');
      return;
    }

    setIsLoading(true);

    try {
      const newVideo = {
        title: title.trim(),
        type: 'vimeo',
        videoId: videoId.trim(),
        date: new Date().toISOString(),
      };

      const updatedVideos = [...(currentVideos || []), newVideo];

      await store.dispatch(
        updateBoard(boardId, { videos: updatedVideos }, false)
      );

      successSwal(t('VideoAddedSuccessfully') || 'Video added successfully');
      handleClose();
    } catch (error) {
      errorSwal(t('ErrorAddingVideo') || 'Error adding video');
    } finally {
      setIsLoading(false);
    }
  };

  const handleShowPreview = () => {
    if (showPreview) {
      setShowPreview(false);
      return;
    }

    const trimmedVideoId = videoId.trim();
    if (!trimmedVideoId) {
      setValidationError('Video ID is required');
      return;
    }

    setValidationError(null);

    const validation = validateVimeoId(trimmedVideoId);

    if (validation.valid) {
      setValidationError(null);
      setIsValidated(true);
      setShowPreview(true);
    } else {
      setValidationError(
        validation.error || t('InvalidVideoId') || 'Invalid video ID'
      );
      setIsValidated(false);
      setShowPreview(false);
    }
  };

  const isValidVideoId = videoId.trim().length > 0;

  return (
    <>
      <Modal.Header>
        <h5 className="modal-title">{t('AddVideo') || 'Add Video'}</h5>
        <a className="close cursor-pointer" onClick={handleClose}>
          <em className="icon ni ni-cross" />
        </a>
      </Modal.Header>

      <Modal.Body>
        <form
          className="form-validate is-alter"
          onSubmit={(e) => {
            e.preventDefault();
            handleSave();
          }}
        >
          <div className="form-group">
            <label className="form-label" htmlFor="video-title">
              {t('Title') || 'Title'}
            </label>
            <div className="form-control-wrap">
              <input
                type="text"
                className="form-control"
                id="video-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={t('EnterVideoTitle') || 'Enter video title'}
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="video-id">
              {t('VideoId') || 'Video ID'}
            </label>
            <div className="form-control-wrap">
              <input
                type="text"
                className="form-control"
                id="video-id"
                value={videoId}
                onChange={(e) => {
                  setVideoId(e.target.value);
                  setShowPreview(false);
                  setValidationError(null);
                  setIsValidated(false);
                }}
                placeholder={t('EnterVimeoVideoId') || 'Enter Vimeo video ID'}
                required
              />
            </div>
            <div className="form-note mt-1">
              {t('VimeoVideoIdNote') ||
                'Enter the Vimeo video ID (e.g., 123456789)'}
            </div>
            {validationError && (
              <div className="alert alert-icon alert-danger mt-2" role="alert">
                <em className="icon ni ni-alert-circle" />
                <strong>{validationError}</strong>
              </div>
            )}
          </div>

          {isValidVideoId && (
            <div className="form-group mt-4">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <label className="form-label mb-0">
                  {t('Preview') || 'Preview'}
                </label>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary"
                  onClick={handleShowPreview}
                >
                  <em
                    className={`icon ni ni-${
                      showPreview ? 'eye-off' : 'eye'
                    } mr-1`}
                  />
                  <span>
                    {showPreview
                      ? t('HidePreview') || 'Hide Preview'
                      : t('ShowPreview') || 'Show Preview'}
                  </span>
                </button>
              </div>
              {showPreview && isValidated && (
                <div className="card card-bordered p-0 overflow-hidden">
                  <style>
                    {`
                      .video-preview-modal .video-responsive {
                        min-height: 0 !important;
                      }
                    `}
                  </style>
                  <div className="video-preview-modal">
                    <VimeoVideoPlayer videoId={videoId.trim()} />
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="form-group mt-4">
            <button
              type="submit"
              className="btn btn-lg btn-primary"
              disabled={!isValidVideoId || !title.trim() || isLoading}
            >
              {isLoading ? t('Saving') || 'Saving...' : t('Save') || 'Save'}
            </button>
            <button
              type="button"
              className="btn btn-lg btn-link ml-2"
              onClick={handleClose}
              disabled={isLoading}
            >
              {t('Cancel') || 'Cancel'}
            </button>
          </div>
        </form>
      </Modal.Body>
    </>
  );
};

export default AddVideoModal;
